import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageTransition, FadeIn } from '../components/PageTransition';
import { BackButton } from '../components/BackButton';
import { X, Play } from 'lucide-react';

const videoData = {
  tops: [
    { 
      id: 'shirts', 
      title: 'Shirts', 
      description: 'Collared garments featuring a front button placket and structured silhouette, designed for smart casual, business casual, and everyday styling.',
      embedUrl: 'https://www.canva.com/design/DAHBxPtWKFg/Sm-WyRg2zDjujmIFDfs8tg/watch?embed', 
      subtypes: ['Flannels and checks', 'Solid shirts', 'Resort shirts', 'Linen shirts', 'Striped shirts', 'Printed shirts', 'Formal shirts'] 
    },
    { 
      id: 'shackets', 
      title: 'Shackets', 
      description: 'A shirt-jacket hybrid made from heavier fabrics, combining the structure of a shirt with the functionality of light outerwear for layering.',
      embedUrl: 'https://www.canva.com/design/DAHBwSk7rSY/mgmBBcaiU9o7umKnjVev4w/watch?embed' 
    },
    { 
      id: 'polo', 
      title: 'Polo', 
      description: 'Knitted tops with a soft collar and short button placket, offering a refined casual look suitable for both casual and smart casual occasions.',
      embedUrl: 'https://www.canva.com/design/DAHBwuyBEI8/LsUq-c4TcWI3OWEnhoj4Gg/watch?embed' 
    },
    { 
      id: 'tshirt', 
      title: 'T-shirt', 
      description: 'Knitted casual tops with a crew neckline and short sleeves, designed for comfort, versatility, and everyday styling.',
      embedUrl: 'https://www.canva.com/design/DAHBwbt_94w/97R_EFj_DQ9Qrp4cIr3COg/watch?embed' 
    },
    { 
      id: 'hoodies', 
      title: 'Hoodies', 
      description: 'Casual knit garments featuring an attached hood and often a kangaroo pocket, designed for warmth and relaxed styling.',
      embedUrl: 'https://www.canva.com/design/DAHBwoVLdgU/_DRHQ_9bj53VejERVGHN5Q/watch?embed' 
    },
    { 
      id: 'sweatshirt', 
      title: 'Sweatshirts & Pullovers', 
      description: 'Long-sleeve knit garments without a front opening, designed to provide warmth and comfort while being suitable for casual layering.',
      embedUrl: 'https://www.canva.com/design/DAHBw2V9RBU/vhFDJw7JjSXOq6LLX3L4Iw/watch?embed' 
    },
    { 
      id: 'jackets', 
      title: 'Jackets', 
      description: 'Structured outerwear designed to be worn over other garments, providing warmth, protection, and an additional style layer.',
      embedUrl: 'https://www.canva.com/design/DAHBxOxpeZ0/QrG-HyMLA7buddm-DR6GWA/watch?embed' 
    },
  ],
  bottoms: [
    { 
      id: 'cargos', 
      title: 'Cargos', 
      description: 'Relaxed-fit trousers featuring multiple utility pockets, combining functionality with a casual, utilitarian aesthetic.',
      embedUrl: 'https://www.canva.com/design/DAHBxIvPXhs/dzXPfqLOyXgnZCraz0-hIA/watch?embed' 
    },
    { 
      id: 'trousers', 
      title: 'Trousers', 
      description: 'Tailored full-length pants designed with a clean, structured silhouette suitable for smart casual and semi-formal dressing.',
      embedUrl: 'https://www.canva.com/design/DAHBxGokb5Q/uY7RMVdYNSKZ8VSwtmrZgw/watch?embed' 
    },
    { 
      id: 'trackpants', 
      title: 'Track Pants', 
      description: 'Athletic-inspired pants made from flexible fabrics, designed for ease of movement and casual comfort.',
      embedUrl: 'https://www.canva.com/design/DAHBxO9TLa8/E0A-8CjlzVMNcuqCJCxEAA/watch?embed' 
    },
    { 
      id: 'denim', 
      title: 'Denim', 
      description: 'Classic denim jeans offering durable fabric, modern fits, and versatile styling options.',
      embedUrl: 'https://www.canva.com/design/DAHBxImDUU0/-w0gXAAzimhDsu3oh_GjBA/watch?embed' 
    },
    { 
      id: 'shorts', 
      title: 'Shorts', 
      description: 'Casual summer essentials designed for comfort, breathability, and relaxed everyday wear.',
      embedUrl: 'https://www.canva.com/design/DAHBxF0h2os/qi3hRXW1PtNinnz9Wp1qqg/watch?embed' 
    },
  ]
};

const VideoItem = ({ item, onClick }) => (
  <motion.div
    onClick={() => onClick(item)}
    className="group cursor-pointer"
    whileHover={{ x: 8 }}
    transition={{ duration: 0.3 }}
    data-testid={`video-item-${item.id}`}
  >
    <div className="flex items-start gap-4 py-5 border-b border-[rgba(26,26,26,0.1)] group-hover:border-[#1A1A1A] transition-colors duration-300">
      <div className="w-10 h-10 border border-[rgba(26,26,26,0.2)] flex items-center justify-center group-hover:bg-[#1A1A1A] group-hover:border-[#1A1A1A] transition-all duration-300 flex-shrink-0 mt-1">
        <Play size={14} className="text-[#1A1A1A] group-hover:text-white transition-colors duration-300" />
      </div>
      <div className="flex-1">
        <h4 className="text-sm font-medium tracking-wide text-[#1A1A1A] mb-1">
          {item.title}
        </h4>
        <p className="text-xs text-[#1A1A1A]/60 leading-relaxed">
          {item.description}
        </p>
        {item.subtypes && (
          <p className="text-[10px] text-[#D32F2F]/70 mt-2">
            {item.subtypes.join(' • ')}
          </p>
        )}
      </div>
    </div>
  </motion.div>
);

const VideoModal = ({ video, onClose }) => {
  const watchUrl = video.embedUrl.replace('/watch?embed', '/watch');
  
  return (
    <motion.div
      className="fixed inset-0 bg-[#1A1A1A]/90 z-50 flex items-center justify-center p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-2xl bg-[#F8F6F2]"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-[#D32F2F] transition-colors duration-300"
          data-testid="close-video-modal"
        >
          <X size={28} />
        </button>
        <div className="p-8 text-center">
          <h3 className="text-xl font-medium tracking-wide text-[#1A1A1A] mb-2">{video.title}</h3>
          <p className="text-sm text-[#1A1A1A]/60 mb-6">{video.description}</p>
          {video.subtypes && (
            <p className="text-xs text-[#D32F2F]/70 mb-8">
              {video.subtypes.join(' • ')}
            </p>
          )}
          <div className="relative aspect-video bg-[#1A1A1A]/5 border border-[rgba(26,26,26,0.1)] mb-6 flex items-center justify-center">
            <div className="text-center">
              <Play size={48} className="mx-auto mb-4 text-[#D32F2F]" />
              <p className="text-sm text-[#1A1A1A]/60">Click below to watch training video</p>
            </div>
          </div>
          <a
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F8F6F2] px-8 py-4 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#D32F2F] transition-colors duration-500"
            data-testid="watch-video-link"
          >
            <Play size={16} />
            Watch Video on Canva
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ApparelPage = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  return (
    <PageTransition className="min-h-screen bg-[#F8F6F2] py-24 px-6">
      <div className="grain-overlay" />
      <BackButton to="/what/product" label="Back" />

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#1A1A1A]/60 mb-4">
            Product Training
          </h2>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight text-[#1A1A1A]">
            Apparel
          </h1>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24">
          {/* TOPS Column */}
          <FadeIn delay={0.2}>
            <div className="border-t-2 border-[#1A1A1A] pt-6">
              <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1A1A1A] mb-8">
                Tops
              </h3>
              <div className="space-y-1">
                {videoData.tops.map((item) => (
                  <VideoItem 
                    key={item.id} 
                    item={item} 
                    onClick={setSelectedVideo}
                  />
                ))}
              </div>
            </div>
          </FadeIn>

          {/* BOTTOMS Column */}
          <FadeIn delay={0.4}>
            <div className="border-t-2 border-[#1A1A1A] pt-6">
              <h3 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1A1A1A] mb-8">
                Bottoms
              </h3>
              <div className="space-y-1">
                {videoData.bottoms.map((item) => (
                  <VideoItem 
                    key={item.id} 
                    item={item} 
                    onClick={setSelectedVideo}
                  />
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      <AnimatePresence>
        {selectedVideo && (
          <VideoModal 
            video={selectedVideo} 
            onClose={() => setSelectedVideo(null)} 
          />
        )}
      </AnimatePresence>
    </PageTransition>
  );
};

export default ApparelPage;
